import React, { useState, useEffect, useRef } from "react";
import Scanner from "../components/Scanner";
import CamScanner from "../components/CamScanner";
import Loading from "../components/Loading";
import "./Checkout.css";
import { apiFetch } from "../components/api"

function CheckoutPage() {
  const [items, setItems] = useState([]);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [newQty, setNewQty] = useState("");
  const [logs, setLogs] = useState([]);
  const [logCollapsed, setLogCollapsed] = useState(false);
  const [flashRowId, setFlashRowId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [restored, setRestored] = useState(false);
  const headerCbRef = useRef(null);

  const scannerRef = useRef(null);

  const allSelected   = cart.length > 0 && selectedIds.size === cart.length;
  const someSelected  = selectedIds.size > 0 && !allSelected;
  const username = localStorage.getItem("username");        
  const STORAGE_KEY = `checkout_cart_${username || "guest"}`;
  const editInputRef = useRef(null);

  const [showScanner, setShowScanner] = useState(false);
  const [isMobile, setIsMobile] = useState(false);


  const focusScanner = () => {
    if (scannerRef.current) scannerRef.current.focus();
  };

  const handleDetected = (code) => {
    setShowScanner(false);
    handleScan(code);
  };

  useEffect(() => {
    const ua = navigator.userAgent || "";
    const uaMobile = /Android|iPhone|iPad|iPod/i.test(ua);

    const check = () => {
      const narrow = window.innerWidth <= 768;  // 폭 768px 이하를 모바일로 간주
      setIsMobile(uaMobile || narrow);
    };

    check(); // 처음 한 번
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  //브라우저 탭 or 다른창 갔다올때 다시 포커스
  useEffect(() => {
    const handleFocus = () => {
      if (scannerRef.current) {
        // 혹시 렌더링 타이밍 때문에 늦게 잡히는 경우 대비해 지연 포커스
        setTimeout(() => {
          scannerRef.current.focus();
        }, 100);
      }
    };

    window.addEventListener("focus", handleFocus);

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        handleFocus();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const addLog = (msg, type = "info") => {
    setLogs((prev) => [
      ...prev,
      { id: crypto.randomUUID(), msg, type, ts: Date.now() },
    ]);
  };

  const formatLog = (log) => {
    const date = new Date(log.ts);
    const hh = String(date.getHours()).padStart(2, "0");
    const mm = String(date.getMinutes()).padStart(2, "0");
    const ss = String(date.getSeconds()).padStart(2, "0");
    return `[${hh}:${mm}:${ss}] ${log.msg}`;
  };

  useEffect(() => {
    if (headerCbRef.current) {
      headerCbRef.current.indeterminate = someSelected;
    }
  }, [someSelected, allSelected, cart.length]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
    focusScanner();
  };

  const toggleSelectAll = (checked) => {
    if (checked) setSelectedIds(new Set(cart.map(c => c.id)));
    else setSelectedIds(new Set());
    focusScanner();
  };

  useEffect(() => {
    if (loading || !Array.isArray(items)) return;

    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      // console.log("[LS] restore raw:", raw);

      if (raw) {
        if (window.confirm("이전에 담긴 장바구니가 있습니다. 비우시겠습니까?")) {
          localStorage.removeItem(STORAGE_KEY);
          setCart([]);
          addLog("이전 장바구니 비움", "info");
          setRestored(true);
          focusScanner();
          return; // 여기서 종료(복원 안 함)
        }

        const saved = JSON.parse(raw); // [{id, qty}]
        if (Array.isArray(saved)) {
          const merged = [];
          for (const s of saved) {
            const found = items.find(it => String(it.id) === String(s.id));
            if (!found) continue;
            const qty = Math.max(1, Math.min(Number(s.qty || 1), Number(found.stock || 0)));
            if (qty < 1) continue;
            merged.push({ ...found, qty });
          }
          if (merged.length > 0) {
            setCart(merged);
            addLog(`이전 장바구니를 불러왔습니다 (${merged.length}건)`, "info");
            // console.log("[LS] restored cart:", merged);
          }
        }
      }
    } catch (e) {
      console.error("[LS] restore failed:", e);
      addLog("로컬 복구 실패(콘솔 확인)", "error");
    } finally {
      setRestored(true); // ✅ 복구 시도 끝 (성공/실패 상관없이)
    }
  }, [loading, items, STORAGE_KEY]);

  useEffect(() => {
    if (!restored) return; // ✅ 복구 끝나기 전에는 저장 금지

    try {
      if (!Array.isArray(cart) || cart.length === 0) {
        // console.log("[LS] empty cart → remove", STORAGE_KEY);
        localStorage.removeItem(STORAGE_KEY);
        return;
      }
      const payload = JSON.stringify(cart.map(({ id, qty }) => ({ id, qty })));
      localStorage.setItem(STORAGE_KEY, payload);
      // console.log("[LS] saved:", STORAGE_KEY, payload);
    } catch (e) {
      console.error("[LS] save failed:", e);
      addLog("로컬 저장 실패(콘솔 확인)", "error");
    }
  }, [cart, STORAGE_KEY, restored]);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const j = await apiFetch("/api/inventory/", { cache: "no-store" });
        if (!alive) return;
        setItems(Array.isArray(j?.data) ? j.data : []);
      } catch (e) {
        if (!alive) return;
        setError(e?.message || "서버 응답 실패");
        setError(`재고리스트 로드 에러 : ${e?.message || "알 수 없는 오류"}`);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const refreshInventory = async () => {
    const j = await apiFetch("/api/inventory/", { cache: "no-store" });
    setItems(Array.isArray(j?.data) ? j.data : []);
  };

  const handleScan = (code) => {
    if (!Array.isArray(items)) {
      addLog("재고 목록 불러오기 실패", "error");
      return;
    }

    const cleanCode = code.replace(/[\t\r\n]+/g, "").trim();
    const found = items.find((item) => item.barcode === cleanCode);

    if (!found) {
      addLog(`미등록 바코드: ${cleanCode}`, "error");
      focusScanner();
      return;
    }

    // ✅ 현재 장바구니 기준으로 미리 재고 체크
    const existsNow = cart.find((p) => p.id === found.id);

    if (existsNow) {
      // 이미 담겨 있는 상태에서 +1 했을 때 재고 초과면 그냥 컷 → 선택도 안 건드림
      if (existsNow.qty + 1 > found.stock) {
        addLog(`재고 부족: ${found.product__name}`, "error");
        focusScanner();
        return;
      }
    } else {
      // 장바구니에 없는데 재고 자체가 0이면 컷
      if (found.stock < 1) {
        addLog(`재고 없음: ${found.product__name}`, "error");
        focusScanner();
        return;
      }
    }

    // ✅ 여기까지 왔으면 실제로 장바구니가 변경되는 성공 케이스
    setCart((prev) => {
      const exists = prev.find((p) => p.id === found.id);
      if (exists) {
        const updated = [
          { ...exists, qty: exists.qty + 1 },
          ...prev.filter((p) => p.id !== found.id),
        ];
        addLog(`수량 +1: ${found.product__name}`, "success");
        setFlashRowId(found.id);
        setTimeout(() => setFlashRowId(null), 700);
        return updated;
      } else {
        addLog(`추가됨: ${found.product__name}`, "success");
        return [{ ...found, qty: 1 }, ...prev];
      }
    });

    // ✅ 성공한 경우에만 선택 추가
    setSelectedIds((prev) => new Set([...prev, found.id]));

    focusScanner();
  };


  const handleCheckout = async () => {
    const username = localStorage.getItem("username");
    const selectedCart = cart.filter((c) => selectedIds.has(c.id));

    if (selectedCart.length === 0) {
      addLog("출고 대상이 선택되지 않았습니다", "error");
      alert("출고 대상이 선택되지 않았습니다");
      focusScanner();
      return;
    }

    setLoading(true);
    try {
      await apiFetch("/api/inventory/checkout/", {
        method: "POST",
        body: selectedCart.map((c) => ({ id: c.id, qty: c.qty, username })),
      });

      alert("출고 완료");
      addLog(`출고 완료 (${selectedCart.length}건)`, "success");
      setCart((prev) => prev.filter((item) => !selectedIds.has(item.id)));
      setSelectedIds(new Set());
      localStorage.removeItem(STORAGE_KEY);

      await refreshInventory();
    } catch (e) {
      alert(`출고 실패 : ${e?.message || "알 수 없는 오류"}`);
      addLog(`출고 실패 : ${e?.message || "알 수 없는 오류"}`, "error");
    } finally {
      setLoading(false);
      focusScanner();
    }
  };

  const handleDeleteSelected = () => {
    if (selectedIds.size === 0) {
      addLog("삭제 대상이 선택되지 않았습니다", "info");
      return;
    }
    if (!window.confirm(`${selectedIds.size}개 품목을 삭제하시겠습니까?`)) return;

    setCart(prev => prev.filter(item => !selectedIds.has(item.id)));
    addLog(`삭제됨: ${selectedIds.size}개 품목`, "success");
    setSelectedIds(new Set());
    focusScanner();
  };

  const handleSaveQty = (id, newQty) => {
    setCart((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          const qtyNum = Number(newQty);
          if (qtyNum > item.stock) {
            addLog(`재고(${item.stock})보다 많은 수량은 불가`, "error");
            return item;
          }
          addLog(`수량 수정: ${item.product__name} → ${qtyNum}`, "success");
          return { ...item, qty: qtyNum };
        }
        return item;
      })
    );
  };

  // if (loading) return <p>재고 데이터를 불러오는 중...</p>;
  if (error) return <p>에러 발생: {error}</p>;

  // ✅ 합계 계산
  const totalKinds = cart.length;
  const totalQty = cart.reduce((acc, c) => acc + c.qty, 0);

  const lastLog = logs[logs.length - 1];

  return (
    <div className="checkout-page">
      <Loading active={loading} />

      <h1 onClick={focusScanner} style={{ cursor: "default" }}>
        🏷️ 출고 관리
      </h1>
      <Scanner ref={scannerRef} onScan={(value) => handleScan(value)} />

      {/* ✅ 로그 섹션 (상태 박스 제거 후 대체) */}
      <div className="log-section">
        <div className="log-header">
          {/* ← 왼쪽: 로그 박스 */}
          <div
            id="log-panel"
            className="log-list"
            onMouseDown={(e) => {
              // 클릭으로 인한 blur 방지
              e.preventDefault();
            }}
            onClick={focusScanner}
          >
            {logCollapsed ? (
              <div className={`log-line ${lastLog ? `log-${lastLog.type}` : "log-info"}`}>
                {lastLog ? formatLog(lastLog) : "로그 없음"}
              </div>
            ) : logs.length > 0 ? (
              [...logs].reverse().map((l) => (
                <div key={l.id} className={`log-line log-${l.type}`}>
                  {formatLog(l)}
                </div>
              ))
            ) : (
              <div className="log-line log-info">로그 없음</div>
            )}
          </div>

          {/* → 오른쪽: 로그 토글 + 카메라 아이콘 버튼 */}
          <div className="log-actions">
            <button
              type="button"
              className="trans-btn icon-text-btn"
              aria-expanded={!logCollapsed}
              aria-controls="log-panel"
              onClick={() => {
                setLogCollapsed(v => !v);
                focusScanner();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setLogCollapsed(v => !v);
                  focusScanner();
                }
              }}
            >
              <span className="btn-icon">
                {logCollapsed ? "📂" : "📜"}
              </span>
              <span className="btn-text">
                {logCollapsed ? "로그 열기" : "로그 접기"}
              </span>
            </button>

            {isMobile && (
              <button
                className="trans-btn icon-text-btn"
                onClick={() => setShowScanner(true)}
              >
                <span className="btn-icon">📷</span>
                <span className="btn-text">카메라</span>
              </button>
            )}

            {showScanner && (
              <CamScanner
                onDetected={handleDetected}
                onClose={() => setShowScanner(false)}
              />
            )}
          </div>
        </div>
      </div>

      <table className="checkout-table">
        <thead>
          <tr>
            <th style={{ width: 36 }}>
              <input
                ref={headerCbRef}
                type="checkbox"
                checked={allSelected}
                onChange={(e) => {
                  toggleSelectAll(e.target.checked)
                  focusScanner();
                }}
                aria-label="전체 선택"
              />
            </th>
            <th onClick={focusScanner} style={{ cursor: "default" }}>품명</th>
            <th onClick={focusScanner} style={{ cursor: "default" }}>패키지</th>
            <th onClick={focusScanner} style={{ cursor: "default" }}>현재 재고</th>
            <th onClick={focusScanner} style={{ cursor: "default" }}>출고 수량</th>
          </tr>
        </thead>
        <tbody>
          {cart.length > 0 ? (
            cart.map((c) => (
              <tr key={c.id} className={c.id === flashRowId ? "row-flash" : ""}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(c.id)}
                    onChange={() => toggleSelect(c.id)}
                    aria-label={`${c.product__name} 선택`}
                  />
                </td>
                <td>{c.product__name}</td>
                <td>
                  {c.product__package__major_package__name}
                  {c.product__package__option ? `-${c.product__package__option}` : ""}
                </td>
                <td>{c.stock}</td>
                <td style={{ cursor: "pointer" }}
                onClick={() => {
                    if (editingId !== c.id) {
                      setEditingId(c.id);
                      setNewQty(c.qty);
                      // 렌더 직후 입력창에 확실히 포커스
                      setTimeout(() => editInputRef.current?.focus(), 0);
                    }
                  }}
                  onKeyDown={(e) => {
                    if ((e.key === "Enter" || e.key === " ") && editingId !== c.id) {
                      e.preventDefault();
                      setEditingId(c.id);
                      setNewQty(c.qty);
                      setTimeout(() => editInputRef.current?.focus(), 0);
                    }
                  }}
                  role="button"
                >
                  {editingId === c.id ? (
                    <div className="stock-edit">
                      <input
                        ref={editInputRef}
                        type="number"
                        min="1"
                        max={c.stock}
                        value={newQty}
                        onChange={(e) => setNewQty(e.target.value)}
                        className="edit-input"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleSaveQty(c.id, newQty);
                            setEditingId(null);
                            focusScanner();
                          } else if (e.key === "Escape") {
                            setEditingId(null);
                            focusScanner();
                          }
                        }}
                      />
                      <button
                        className="save-btn"
                        onClick={() => {
                          handleSaveQty(c.id, newQty);
                          setEditingId(null);
                          focusScanner();
                        }}
                      >
                        확인
                      </button>
                      {/* <button
                        className="delete-btn"
                        onClick={() => {
                          if (window.confirm(`${c.product__name} 품목을 삭제하시겠습니까?`)) {
                            setCart((prev) => prev.filter((item) => item.id !== c.id));
                            addLog(`삭제됨: ${c.product__name}`, "info");
                            focusScanner();
                          }
                        }}
                      >
                        삭제
                      </button> */}
                    </div>
                  ) : (
                    <div className="stock-edit">
                      <span
                        onClick={() => {
                          setEditingId(c.id);
                          setNewQty(c.qty);
                        }}
                      >
                        {c.qty}
                      </span>
                      {/* <button
                        className="delete-btn"
                        onClick={() => {
                          if (window.confirm(`${c.product__name} 품목을 삭제하시겠습니까?`)) {
                            setCart((prev) => prev.filter((item) => item.id !== c.id));
                            addLog(`삭제됨: ${c.product__name}`, "info");
                            focusScanner();
                          }
                        }}
                      >
                        삭제
                      </button> */}
                    </div>
                  )}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="5">출고할 품목이 없습니다</td>
            </tr>
          )}
        </tbody>

        {cart.length > 0 && (
          <tfoot>
            <tr style={{ background: "#edf2f7", fontWeight: "bold" }}>
              <td onClick={focusScanner} style={{ cursor: "default" }}>-</td>
              <td onClick={focusScanner} style={{ cursor: "default" }}>합계</td>
              <td onClick={focusScanner} style={{ cursor: "default" }}>{totalKinds} 종류</td>
              <td onClick={focusScanner} style={{ cursor: "default" }}>{totalQty} 개</td>
              <td onClick={focusScanner} style={{ cursor: "default" }}>-</td>
            </tr>
          </tfoot>
        )}
      </table>

      <div className="actions">
        <button className="delete-selected-btn" onClick={handleDeleteSelected}>
          삭제
        </button>
        <button
          className="checkout-btn"
          onClick={handleCheckout}
          disabled={selectedIds.size === 0}
          title={selectedIds.size === 0 ? "출고 대상이 선택되지 않았습니다" : "출고"}
        >
          출고
        </button>
      </div>
    </div>
  );
}

export default CheckoutPage;